import { useEffect, useId, useRef, type ReactNode } from 'react';
import {
  ArrowClockwise,
  CheckCircle,
  CircleNotch,
  Info,
  WarningCircle,
  X
} from '@phosphor-icons/react';
import { formatCny } from '@longjie/shared';

let openModalCount = 0;
let bodyOverflowBeforeModals = '';
let htmlOverflowBeforeModals = '';

export function BrandLogo() {
  return <img className="brand-symbol" src="/longjie-logo.png" alt="龙杰 Logo" />;
}

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand-mark">
      <BrandLogo />
      {!compact && (
        <span className="brand-mark__name">
          <strong>阳江龙杰</strong>
          <span className="brand-mark__subtitle">日丰订货系统</span>
        </span>
      )}
    </div>
  );
}

export function Button({
  children,
  variant = 'primary',
  loading = false,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      className={`button button--${variant} ${className}`}
      disabled={loading || props.disabled}
      aria-busy={loading || undefined}
    >
      {loading && <CircleNotch className="spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children
}: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function EmptyState({
  title,
  description,
  action
}: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="empty-state__pipe" aria-hidden="true"><i /><i /></span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadError({
  title,
  description,
  onRetry
}: {
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <div className="load-error" role="alert">
      <WarningCircle weight="fill" aria-hidden="true" />
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        <ArrowClockwise />重新加载
      </Button>
    </div>
  );
}

export function StatusBadge({
  children,
  tone = 'neutral'
}: { children: ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' }) {
  return <span className={`status status--${tone}`}>{children}</span>;
}

export function Money({ cents, className = '' }: { cents: number; className?: string }) {
  return <span className={`money ${className}`}>{formatCny(cents)}</span>;
}

export function Modal({
  title,
  description,
  children,
  onClose,
  footer,
  className = '',
  initialFocus = 'input'
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  className?: string;
  initialFocus?: 'input' | 'dialog';
}) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const activeDialog = dialog;
    const backdrop = backdropRef.current;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const siblings = backdrop?.parentElement ? [...backdrop.parentElement.children] : [];
    const backdropIndex = backdrop ? siblings.indexOf(backdrop) : -1;
    const backgroundElements = backdrop?.parentElement
      ? siblings
        .filter((element): element is HTMLElement => {
          if (!(element instanceof HTMLElement) || element === backdrop) return false;
          if (!element.classList.contains('modal-backdrop')) return true;
          return siblings.indexOf(element) < backdropIndex;
        })
        .map((element) => ({
          element,
          inert: element.inert,
          ariaHidden: element.getAttribute('aria-hidden')
        }))
      : [];
    for (const { element } of backgroundElements) {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    }
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    const focusableElements = () => [...activeDialog.querySelectorAll<HTMLElement>(focusableSelector)]
      .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');

    const frame = window.requestAnimationFrame(() => {
      if (initialFocus === 'dialog') {
        activeDialog.focus({ preventScroll: true });
        return;
      }
      const preferred = activeDialog.querySelector<HTMLElement>(
        '[data-autofocus], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      (preferred ?? focusableElements()[0] ?? activeDialog).focus();
    });

    function handleKeyDown(event: KeyboardEvent) {
      const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]');
      if (dialogs.item(dialogs.length - 1) !== activeDialog) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        activeDialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === activeDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (openModalCount === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
      htmlOverflowBeforeModals = document.documentElement.style.overflow;
    }
    openModalCount += 1;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      openModalCount = Math.max(0, openModalCount - 1);
      for (const { element, inert, ariaHidden } of backgroundElements) {
        if (!element.isConnected) continue;
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      }
      if (openModalCount === 0) {
        document.body.style.overflow = bodyOverflowBeforeModals;
        document.documentElement.style.overflow = htmlOverflowBeforeModals;
      }
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [initialFocus]);

  return (
    <div ref={backdropRef} className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section
        ref={dialogRef}
        className={`modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X /></button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </section>
    </div>
  );
}

export function Toast({
  message,
  tone = 'success',
  onClose
}: { message: string; tone?: 'success' | 'error' | 'info'; onClose: () => void }) {
  const Icon = tone === 'success' ? CheckCircle : tone === 'error' ? WarningCircle : Info;
  return (
    <div className={`toast toast--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon weight="fill" />
      <span>{message}</span>
      <button onClick={onClose} aria-label="关闭提示"><X /></button>
    </div>
  );
}
